import { ChangeEvent, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import QuestionSettingsPanel from './QuestionSettingsPanel';
import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { InputType, QuestionType } from '@act/shared/types';
import CheckpointEditor from './CheckpointEditor';
import PromptEditor from './PromptEditor';
import NextQuestionSelector from './NextQuestionSelector';

interface QuestionFormProps {
  question: SurveyV2.CreateQuestionJSONDto;
  questionCount: number;
  isActionSurvey: boolean;
  onQuestionChange: (
    question: SurveyV2.CreateQuestionJSONDto & { id?: number }
  ) => void;
  formErrors?: any; // Form validation errors from React Hook Form
  questionIndex?: number; // Index of this question in the questions array
}

const QuestionForm = ({
  question,
  questionCount,
  isActionSurvey,
  onQuestionChange,
  formErrors,
  questionIndex,
}: QuestionFormProps) => {
  const [hasNextQuestion, setHasNextQuestion] = useState(
    !!question.nextQuestionKey
  );

  const handleQuestionTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    textType: 'text' | 'title'
  ) => {
    onQuestionChange({
      ...question,
      [textType]: e.target.value || '',
    });
  };

  const handleIsOptionalChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isRequired = !e.target.checked;

    const validationConfiguration = isRequired
      ? {
          validationConfiguration: {
            isRequired: true,
          },
        }
      : {
          validationConfiguration: undefined,
        };

    onQuestionChange({
      ...question,
      ...validationConfiguration,
      isRequired,
    });
  };

  const handleHasNextQuestionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const shouldHaveNextQuestion = e.target.checked;
    setHasNextQuestion(shouldHaveNextQuestion);

    if (shouldHaveNextQuestion) {
      onQuestionChange({
        ...question,
        nextQuestionKey: '',
      });
    } else {
      onQuestionChange({
        ...question,
        nextQuestionKey: undefined,
      });
    }
  };

  const handleQuestionSearchKeyChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onQuestionChange({
      ...question,
      searchKey: e.target.value || '',
    });
  };

  const handleNextQuestionChange = (nextQuestionKey: string) => {
    onQuestionChange({
      ...question,
      nextQuestionKey,
    });
  };

  const handleHasPromptChange = (e: ChangeEvent<HTMLInputElement>) => {
    const hasPrompt = e.target.checked;

    if (hasPrompt) {
      onQuestionChange({
        ...question,
        prompt: SurveyV2.DEFAULT_PROMPT,
      });
    } else {
      onQuestionChange({
        ...question,
        prompt: undefined,
      });
    }
  };

  const handlePromptChange = (prompt: SurveyV2.CreatePromptJSONDto) => {
    onQuestionChange({
      ...question,
      prompt,
    });
  };

  const handleOptionsChange = (options: SurveyV2.CreateOptionJSONDto[]) => {
    onQuestionChange({
      ...question,
      options,
    });
  };

  const handleHasCheckpointChange = (e: ChangeEvent<HTMLInputElement>) => {
    const hasCheckpoint = e.target.checked;

    if (hasCheckpoint) {
      onQuestionChange({
        ...question,
        checkpoint: SurveyV2.DEFAULT_CHECKPOINT,
      });
    } else {
      onQuestionChange({
        ...question,
        checkpoint: undefined,
      });
    }
  };

  const handleCheckpointChange = (
    checkpoint: SurveyV2.CreateCheckpointJSONDto
  ) => {
    onQuestionChange({
      ...question,
      checkpoint: {
        ...checkpoint,
        dueDateOffsetDays: checkpoint.dueDateOffsetDays ?? 0,
      },
    });
  };

  const handleQuestionTypeChange = (type: QuestionType) => {
    const questionRequiresOptions =
      type === 'checkbox' || type === 'radio' || type === 'select';

    onQuestionChange({
      ...question,
      type: type as InputType,
      validationConfiguration: {
        ...question.validationConfiguration,
        minDateOffset: undefined,
        maxDateOffset: undefined,
      },
      options: questionRequiresOptions
        ? [
            SurveyV2.DEFAULT_OPTION,
            { ...SurveyV2.DEFAULT_OPTION, sortOrder: 2 },
          ]
        : [],
    });
  };

  const handleDateOffsetChange = (
    e: ChangeEvent<HTMLInputElement>,
    offsetType: 'minDateOffset' | 'maxDateOffset'
  ) => {
    const value = e.target.value;
    const numberValue = value === '' ? undefined : Number(value);

    onQuestionChange({
      ...question,
      validationConfiguration: {
        ...question.validationConfiguration,
        [offsetType]: numberValue,
      },
    });
  };

  const handleReportingSettingsChange = (
    e: ChangeEvent<HTMLInputElement>,
    reportingType:
      | 'isActionTakenDateQuestion'
      | 'isExpectedCompletionDateQuestion'
  ) => {
    const isReportingQuestion = e.target.checked;

    onQuestionChange({
      ...question,
      [reportingType]: isReportingQuestion,
    });
  };

  return (
    <form>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          justifyItems: 'flex-start',
          textAlign: 'left',
        }}
      >
        {!question.text && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              padding: '0 8px',
            }}
          >
            <Typography>Get started by typing the question text!</Typography>
            <PanToolAltIcon
              htmlColor="#ff7b65"
              sx={{
                rotate: '180deg',
                '@keyframes bounce': {
                  '0%': { transform: 'translateY(0%)' },
                  '25%': { transform: 'translateY(-10%)' },
                  '50%': { transform: 'translateY(10%)' },
                  '75%': { transform: 'translateY(-10%)' },
                  '100%': { transform: 'translateY(-0%)' },
                },
                animation: 'bounce 2s ease infinite',
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
            padding: '0',
          }}
        >
          <TextField
            label="Question title"
            value={question.title || ''}
            onChange={e => handleQuestionTextChange(e, 'title')}
            onKeyDown={e => {
              if (question.title && e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            sx={{ flex: 1, marginBottom: 0 }}
            fullWidth
          />
          <TextField
            label="Question text"
            value={question.text || ''}
            onChange={e => handleQuestionTextChange(e, 'text')}
            onKeyDown={e => {
              if (question.text && e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            sx={{ flex: 1, marginBottom: 0 }}
            fullWidth
          />
          <TextField
            label="Question Search Key"
            value={question.searchKey ?? ''}
            onChange={handleQuestionSearchKeyChange}
            sx={{ flex: 1, marginBottom: 0 }}
            fullWidth
            helperText="Advanced: Unique identifier for this question (used for lookups)"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '0 0 8px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Box sx={{ width: '50%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!question.isRequired || false}
                      onChange={handleIsOptionalChange}
                    />
                  }
                  label="Optional question"
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasNextQuestion}
                    onChange={handleHasNextQuestionChange}
                    disabled={questionCount <= 1}
                  />
                }
                label="Has next question"
              />
            </Box>
            {hasNextQuestion && (
              <NextQuestionSelector
                questionKey={question.key}
                nextQuestionKey={question.nextQuestionKey}
                onChange={handleNextQuestionChange}
              />
            )}
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Box sx={{ width: '50%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!question.prompt || false}
                      onChange={handleHasPromptChange}
                    />
                  }
                  label="Has prompt"
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!question.checkpoint || false}
                    onChange={handleHasCheckpointChange}
                  />
                }
                label="Has checkpoint"
              />
            </Box>
          </Box>
          {question.prompt && (
            <PromptEditor
              prompt={question.prompt}
              onChange={handlePromptChange}
              errors={formErrors}
              fieldPath={
                questionIndex !== undefined
                  ? `surveyContent.surveyVersion.questions.${questionIndex}.prompt`
                  : undefined
              }
            />
          )}
          {question.checkpoint && (
            <CheckpointEditor
              checkpoint={question.checkpoint}
              onChange={handleCheckpointChange}
            />
          )}
          {isActionSurvey && question.type === 'date' && (
            <Box>
              <Divider
                sx={{
                  margin: '8px 0',
                  opacity: 1,
                  '&::before, &::after': {
                    borderTopWidth: '2px',
                  },
                }}
              >
                Reporting settings
              </Divider>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <Box sx={{ width: '50%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={question.isActionTakenDateQuestion || false}
                        onChange={e =>
                          handleReportingSettingsChange(
                            e,
                            'isActionTakenDateQuestion'
                          )
                        }
                        disabled={!isActionSurvey}
                      />
                    }
                    label="Is action taken date"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        question.isExpectedCompletionDateQuestion || false
                      }
                      onChange={e =>
                        handleReportingSettingsChange(
                          e,
                          'isExpectedCompletionDateQuestion'
                        )
                      }
                      disabled={!isActionSurvey}
                    />
                  }
                  label="Is expected completion date"
                />
              </Box>
            </Box>
          )}
          <Divider
            sx={{
              margin: isActionSurvey ? '0' : '8px 0',
            }}
          />
          <QuestionSettingsPanel
            question={question}
            questionCount={questionCount}
            isActionSurvey={isActionSurvey}
            onQuestionTypeChange={handleQuestionTypeChange}
            onDateOffsetChange={handleDateOffsetChange}
            onOptionsChange={handleOptionsChange}
            formErrors={formErrors}
            questionIndex={questionIndex}
          />
        </Box>
      </Box>
    </form>
  );
};

export default QuestionForm;
